import { z } from 'zod';

import { ACCEPTED_PRIVACY_POLICY_FIELD } from '@/components/form/fieldDefs/signUpPrivacyAcceptance';
import { buildFormSchema } from '@/utils/validation/formSchemaKit';
import {
  assertPasswordsMatch,
  buildCommonValidationMessages,
  emailField,
  isValidEmail,
  optionalString,
  passwordConfirmField,
  passwordField,
  phoneField,
  requiredArrayField,
  requiredBoolean,
  requiredString,
  withMaxLength,
} from '@/utils/validation/schemaHelpers';

import type { FormSchema } from '@/utils/validation/formSchemaKit';
import type { TFunction } from 'i18next';

export const buildRecruiterInviteFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema(
    {
      name: withMaxLength(
        requiredString(messages.required),
        128,
        messages.maxLength(128),
      ),
      title: withMaxLength(
        requiredString(messages.required),
        255,
        messages.maxLength(255),
      ),
      email: emailField(messages),
      city: requiredString(messages.required),
      recruiter_type: requiredString(messages.required),
      company: {
        schema: z.union([z.string(), z.null()]).optional(),
        isRequired: (values: Record<string, unknown>) =>
          values.recruiter_type !== 'freelance',
      },
      phone: phoneField(messages, true),
    },
    (schema) =>
      schema.superRefine((data, ctx) => {
        if (data.recruiter_type !== 'freelance' && !data.company) {
          ctx.addIssue({
            code: 'custom',
            path: ['company'],
            message: messages.required,
          });
        }
      }),
  );
};

export const buildRecruiterInvitationFormSchema = (
  t: TFunction,
): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema(
    {
      name: withMaxLength(
        requiredString(messages.required),
        128,
        messages.maxLength(128),
      ),
      title: withMaxLength(
        requiredString(messages.required),
        255,
        messages.maxLength(255),
      ),
      email: emailField(messages),
      city: requiredString(messages.required),
      recruiter_type: optionalString(),
      company: {
        schema: z.union([z.string(), z.null()]).optional(),
        isRequired: (values: Record<string, unknown>) =>
          values.recruiter_type !== 'freelance',
      },
      phone: phoneField(messages, false),
      password: passwordField(messages),
      password_confirm: passwordConfirmField(messages),
      language: requiredString(messages.required),
      currency: requiredString(messages.required),
      [ACCEPTED_PRIVACY_POLICY_FIELD]: requiredBoolean(messages.required),
    },
    (schema) =>
      schema.superRefine((data, ctx) => {
        assertPasswordsMatch(
          data,
          ctx,
          'password',
          'password_confirm',
          messages.passwordMismatch,
        );
        if (data.recruiter_type !== 'freelance' && !data.company) {
          ctx.addIssue({
            code: 'custom',
            path: ['company'],
            message: messages.required,
          });
        }
      }),
  );
};

export const buildRecruiterOnboardFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    specializations: requiredArrayField(messages.required),
    seniority: requiredArrayField(messages.required),
    locations: requiredArrayField(messages.required),
    preference_relevance_interval: requiredString(messages.required),
  });
};

export const buildInviteAgencyEmailFormSchema = (
  t: TFunction,
  options?: { requireAgencyCompany?: boolean },
): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  const requireAgencyCompany = options?.requireAgencyCompany ?? false;
  return buildFormSchema(
    {
      emails: {
        schema: z
          .array(
            z.object({
              email: z.string().optional(),
              name: z.string().optional(),
            }),
          )
          .optional(),
        isRequired: false,
      },
      agency_company: {
        schema: z.string().optional(),
        isRequired: false,
      },
    },
    (schema) =>
      schema.superRefine((data, ctx) => {
        const rows = (data.emails ?? []) as Array<{
          email?: string;
          name?: string;
        }>;
        const hasFilledEmail = rows.some((row) => Boolean(row?.email?.trim()));
        if (!hasFilledEmail) {
          ctx.addIssue({
            code: 'custom',
            path: ['emails', 0, 'email'],
            message: t('INVITE_AT_LEAST_ONE_EMAIL'),
          });
          return;
        }

        rows.forEach((row, index) => {
          const email = String(row?.email ?? '').trim();
          const name = String(row?.name ?? '').trim();
          if (!email && !name) {
            return;
          }
          if (email && !isValidEmail(email)) {
            ctx.addIssue({
              code: 'custom',
              path: ['emails', index, 'email'],
              message: messages.invalidEmail,
            });
          }
          if (email && !name) {
            ctx.addIssue({
              code: 'custom',
              path: ['emails', index, 'name'],
              message: t('INVITE_EMAIL_NAME_REQUIRED'),
            });
          }
        });

        if (requireAgencyCompany) {
          const agencyCompany = String(
            (data as { agency_company?: string }).agency_company ?? '',
          ).trim();
          if (!agencyCompany) {
            ctx.addIssue({
              code: 'custom',
              path: ['agency_company'],
              message: t('RM_SELECT_AGENCY_COMPANY'),
            });
          }
        }
      }),
  );
};
